import React, { useMemo, useState, useEffect } from 'react'
import './dashboard.css'
import AccommodationList from './AccommodationInfoForm';
import ContactInfoForm from './ContactInfoForm';
import StylesForm from './StylesForm';
import TandC from './TandC';
import MetaTagsForm from './MetaTagsForm';
import PaymentSettingsForm from './PaymentSettingsForm';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mantine/core';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import "../properties/propertiesLanding.css";
import useAuth from '../../hooks/useAuth';
import ImageContent from '../dynamic/ImageContent';
import { defaultLanguage } from '../../constants';

export default function Dashboard(props) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { username } = useAuth();

  const isSubTenant = useMemo(() => {
    const host = String(window?.location?.hostname || "").trim().toLowerCase();
    const effective = (host && host !== "localhost" && host !== "127.0.0.1")
      ? host
      : String(username || "").trim().toLowerCase();

    const parts = effective.split(".").filter(Boolean);
    return parts.length > 2;
  }, [username]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [requestedCount, setRequestedCount] = useState(1);
  const [propertyNames, setPropertyNames] = useState([""]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [properties, setProperties] = useState([]);
  const [canAddProperty, setCanAddProperty] = useState(false);
  const [missingRequired, setMissingRequired] = useState({ contact: [], accommodation: [] });
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [addBlockedMessage, setAddBlockedMessage] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const mainDomain = useMemo(() => {
    const main = (properties || []).find((p) => p?.isMain);
    return main?.domain || properties?.[0]?.domain || "";
  }, [properties]);

  useEffect(() => {
    if (isSubTenant) {
      setCanAddProperty(false);
      setMissingRequired({ contact: [], accommodation: [] });
      return;
    }
    if (!username) {
      setCanAddProperty(false);
      setMissingRequired({ contact: [], accommodation: [] });
      return;
    }

    let cancelled = false;

    const isValidEmail = (v) => {
      const s = String(v || "").trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    };

    const isPositiveNumber = (v) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0;
    };

    // Check if a value looks like a default/dummy value
    const isDefaultValue = (v) => {
      const s = String(v || "").trim().toLowerCase();
      const dummyPatterns = ['test', 'example', 'placeholder', 'default', 'sample', 'n/a', 'na', 'none'];
      return s === '' || dummyPatterns.some(pattern => s === pattern);
    };

    const compute = async () => {
      try {
        const art = defaultLanguage;
        const [contactRes, accRes] = await Promise.all([
          axiosPrivate.get("/contact", { params: { username, art, title: "contact" } }),
          axiosPrivate.get("/dashboard", { params: { username, art, title: "accomodationForm" } }),
        ]);

        if (cancelled) return;

        const contact = contactRes?.data?.entry || {};
        const acc = accRes?.data?.entry || {};

        const languages = String(contact?.languages || "").trim();
        const missingContact = [];
        if (!Boolean(languages)) missingContact.push("languages");
        if (!Boolean(String(contact?.name || "").trim()) || isDefaultValue(contact?.name)) missingContact.push("name");
        if (!Boolean(String(contact?.lastname || "").trim()) || isDefaultValue(contact?.lastname)) missingContact.push("lastname");
        if (!Boolean(String(contact?.street || "").trim()) || isDefaultValue(contact?.street)) missingContact.push("street");
        if (!isPositiveNumber(contact?.number) || isDefaultValue(contact?.number)) missingContact.push("number");
        if (!Boolean(String(contact?.city || "").trim()) || isDefaultValue(contact?.city)) missingContact.push("city");
        if (!isPositiveNumber(contact?.zip) || isDefaultValue(contact?.zip)) missingContact.push("zip");
        if (!Boolean(String(contact?.country || "").trim()) || isDefaultValue(contact?.country)) missingContact.push("country");
        if (!isValidEmail(contact?.mail) || isDefaultValue(contact?.mail)) missingContact.push("mail");

        const missingAccommodation = [];
        if (String(acc?.accomodationType || "-1") === "-1" || isDefaultValue(acc?.accomodationType)) missingAccommodation.push("accomodationType");
        if (!Boolean(String(acc?.nameAccommodation || "").trim()) || isDefaultValue(acc?.nameAccommodation)) missingAccommodation.push("nameAccommodation");
        if (!isPositiveNumber(acc?.guests) || isDefaultValue(acc?.guests)) missingAccommodation.push("guests");
        if (!isPositiveNumber(acc?.bedrooms) || isDefaultValue(acc?.bedrooms)) missingAccommodation.push("bedrooms");

        setMissingRequired({ contact: missingContact, accommodation: missingAccommodation });
        setCanAddProperty(Boolean(missingContact.length === 0 && missingAccommodation.length === 0));
      } catch (e) {
        if (!cancelled) {
          setCanAddProperty(false);
          setMissingRequired({ contact: [], accommodation: [] });
        }
      }
    };

    compute();

    return () => {
      cancelled = true;
    };
  }, [axiosPrivate, isSubTenant, username, refreshKey]);

  const scrollToFirstMissing = () => {
    const first = [...(missingRequired?.contact || []), ...(missingRequired?.accommodation || [])][0];
    if (!first) return;
    const el = document.getElementById(first);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") el.focus();
    }
  };

  const getDetailedErrorMessage = () => {
    const missing = [...(missingRequired?.contact || []), ...(missingRequired?.accommodation || [])];
    if (missing.length === 0) return "";
    
    const fieldLabels = {
      name: "First Name",
      lastname: "Last Name",
      street: "Street",
      number: "Street Number",
      city: "City",
      zip: "Postal Code",
      country: "Country",
      mail: "Email Address",
      languages: "Languages",
      accomodationType: "Accommodation Type",
      nameAccommodation: "Accommodation Name",
      guests: "Number of Guests",
      bedrooms: "Number of Bedrooms"
    };
    
    const fieldNames = missing.map(field => fieldLabels[field] || field);
    const missingText = fieldNames.join(", ");
    
    return `Please fill and save all required fields (*) marked below:\n\n${missingText}`;
  };

  const openAddModal = async () => {
    setSubmitError("");
    setSubmitSuccess("");

    if (!canAddProperty) {
      setShowRequiredErrors(true);
      setAddBlockedMessage(getDetailedErrorMessage());
      scrollToFirstMissing();
      return;
    }

    setAddBlockedMessage("");
    setShowRequiredErrors(false);
    setAddModalOpen(true);

    if ((properties || []).length > 0) return;
    try {
      const res = await axiosPrivate.get("/properties");
      const list = Array.isArray(res?.data?.properties) ? res.data.properties : [];
      setProperties(list);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load properties";
      setSubmitError(msg);
    }
  };

  const applyCount = () => {
    const n = Math.max(1, Number(requestedCount) || 1);
    setRequestedCount(n);
    setPropertyNames((prev) => {
      const next = [...(prev || [])];
      while (next.length < n) next.push("");
      return next.slice(0, n);
    });
  };

  const addAnotherRow = () => {
    setRequestedCount((prev) => (Number(prev) || 1) + 1);
    setPropertyNames((prev) => [...(prev || []), ""]);
  };

  const removeRow = (idx) => {
    setPropertyNames((prev) => {
      const next = (prev || []).filter((_, i) => i !== idx);
      setRequestedCount(Math.max(1, next.length));
      return next.length ? next : [""];
    });
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    const names = (propertyNames || []).map((n) => String(n || "").trim()).filter(Boolean);
    if (names.length === 0) {
      setSubmitError("At least one property name is required");
      return;
    }
    if (!mainDomain) {
      setSubmitError("Main property is not available yet");
      return;
    }

    setSubmitting(true);
    try {
      await axiosPrivate.post("/properties/request", {
        mainDomain,
        requestedCount: names.length,
        propertyNames: names,
      });
      setSubmitSuccess("Request submitted. You will be notified once it is approved.");
      setRequestedCount(1);
      setPropertyNames([""]);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to submit property request";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="default">
        <div className="stripe">
          <div className="contact-title">
            <h1>{t("dashboard")}</h1>
          </div>
        </div>

        <Modal
          opened={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          title="Add New Property"
          size="lg"
          centered
        >
          <form onSubmit={submitRequest}>
            <label className="form-label">
              How many properties?
              <input
                type="text"
                value={requestedCount}
                onChange={(e) => setRequestedCount(e.target.value)}
                disabled={submitting}
              />
            </label>

            <div style={{ marginTop: 10, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="properties-landing__actionBtn"
                onClick={applyCount}
                disabled={submitting}
              >
                Apply
              </button>
              <button
                type="button"
                className="properties-landing__actionBtn"
                onClick={addAnotherRow}
                disabled={submitting}
              >
                Add another
              </button>
            </div>

            <div style={{ marginTop: 16 }}>
              {propertyNames.map((val, idx) => (
                <div key={idx} style={{ marginBottom: 12 }}>
                  <label className="form-label">
                    Property name {idx + 1}
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPropertyNames((prev) => (prev || []).map((x, i) => (i === idx ? v : x)));
                      }}
                      disabled={submitting}
                    />
                  </label>

                  {propertyNames.length > 1 && (
                    <button
                      type="button"
                      className="properties-landing__actionBtn"
                      onClick={() => removeRow(idx)}
                      disabled={submitting}
                      style={{ marginTop: 6 }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>

            {submitError && (
              <div className="properties-landing__status properties-landing__status--error">
                {submitError}
              </div>
            )}

            {submitSuccess && (
              <div className="properties-landing__status properties-landing__status--success">
                {submitSuccess}
              </div>
            )}

            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'center' }}>
              <button
                type="submit"
                className="properties-landing__actionBtn"
                disabled={submitting}
              >
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </Modal>

        <hr></hr>
        <div className="container">
          <ContactInfoForm
            showRequiredErrors={showRequiredErrors}
            missingRequired={missingRequired?.contact}
            onSaved={() => {
              setShowRequiredErrors(false);
              setAddBlockedMessage("");
              setRefreshKey((v) => v + 1);
            }}
          />
        </div>

        <hr></hr>
        {isSubTenant && (
          <>
            <div className="container">
              <h2>Destination Card Image</h2>
              <ImageContent
                title="subdomain-card-image"
                username={username}
                type="properties-landing__image"
                smallEditButton={true}
                showPlaceholder={true}
              />
            </div>
            <hr></hr>
          </>
        )}
        <div className="container">
          <AccommodationList
            showRequiredErrors={showRequiredErrors}
            missingRequired={missingRequired?.accommodation}
            onSaved={() => {
              setShowRequiredErrors(false);
              setAddBlockedMessage("");
              setRefreshKey((v) => v + 1);
            }}
          />
        </div>

        <hr></hr>
        <div className="container">
          <MetaTagsForm />
        </div>
        <hr></hr>
        <div className="container">
          <PaymentSettingsForm />
        </div>
        <hr></hr>
        <div className="container">
          <TandC />
        </div>
        <hr></hr>
        <div className="container">
          <StylesForm />
        </div>
        {!isSubTenant && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom : 10 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: '100%', maxWidth: '600px' }}>
              <button
                type="button"
                className={`properties-landing__actionBtn${!canAddProperty ? " properties-landing__actionBtn--disabled" : ""}`}
                onClick={openAddModal}
                aria-disabled={!canAddProperty}
                disabled={!canAddProperty}
              >
                Add New Property
              </button>
              {addBlockedMessage && (
                <div className="error-msg" style={{ width: '100%' }}>
                  {addBlockedMessage}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};